<template>
    <v-card class="flex-grow-1 d-flex flex-column" style="width: 100%; height: 100%;" 
      @keydown.esc="$emit('goBack')">
        <v-toolbar card color="primary subheading" dark height="64" class="flex-grow-0 d-flex">
            <v-layout row align-start justify-right>
                <v-flex xs2 align-self-start>
                    <v-btn icon @click="$emit('goBack')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                </v-flex>
                <v-flex xs8 align-self-center class="text-xs-center">
                    <v-avatar 
                        v-if="item && item.locationHasAlert" 
                        :color="item.locationAlertLevelColor"
                        class="mr-2"
                        size="32"                        
                        >
                    </v-avatar>
                    <span v-if="item" class="title">{{ title }}</span>
                </v-flex>
            </v-layout>
        </v-toolbar>
        <v-container class="pa-2 flex-grow-1 d-flex flex-column" fluid>
            <v-layout row justify-center :class="`flex-grow-0 py-1`">
                <v-flex shrink>
                    <ForecastingItemNavigation v-model="mode"></ForecastingItemNavigation>
                </v-flex>
            </v-layout>
            <v-layout row class="flex-grow-1 d-flex" :style="`${isTabletMode ? `position: relative; width: 100%; height: 100%;` : ``}`">
                <v-flex :style="`${isTabletMode ? `position: absolute; top: 0; bottom: 0; left: 0; right: 0; overflow: auto;` : ``}`">
                    <ForecastingItemDetail v-if="isDetailMode"
                        :variety="varietyEdit"
                        :forecasts="cropForecasts"
                        :loading="loading"
                        @refresh="loadForecastsAndActualsForProfile()">
                    </ForecastingItemDetail>
                    <ForecastingItemNotes v-else-if="isNotesMode"
                        :variety="varietyEdit"
                        :notes="cropNotes">
                    </ForecastingItemNotes>
                    <ForecastingItemProfile v-else-if="isProfileMode"
                        :variety="varietyEdit"
                        :forecasts="cropForecasts"
                        @goBack="$emit('goBack')">
                    </ForecastingItemProfile>
                </v-flex>
            </v-layout>
        </v-container>
    </v-card>
</template>

<script>
import ForecastingItemModes from '@/helpers/forecastingItemModes';
import ForecastingItemNavigation from '@/components/forecasting/ForecastingItemNavigation.vue';
import ForecastingItemDetail from '@/components/forecasting/ForecastingItemDetail.vue';
import ForecastingItemNotes from '@/components/forecasting/forecastingNotes/ForecastingItemNotes.vue';
import ForecastingItemProfile from '@/components/forecasting/ForecastingItemProfile.vue';

import forecastingProfileApi from '@/api/forecastingProfileApi';
import forecastingUtilService from '@/services/forecastingUtilService';

import { mapState } from "vuex";

export default {
    components: {
        ForecastingItemNavigation,
        ForecastingItemDetail,
        ForecastingItemNotes,
        ForecastingItemProfile
    },
    props: {
        cropLocationId: {
            type: Number,
            required: true
        },
        varietyId: {
            type: Number,
            required: true
        },
        routeForecastMode: {
            type: Number,
            required: false
        }
    },
    data(){
        return {
            mode: ForecastingItemModes.Detail,
            loading: false,
            profileForecasts: null,            
            varietyEdit: {}
        };
    },
    computed: {
        ...mapState("ForecastingStore", [
            "varieties",
            "crops",
            "financialYearCalendarData",
            "forecasts",
            "notes"
        ]),
        item(){
            let variety = this.varieties.find(x => x.varietyId == this.varietyId);
            if(variety == null)
                return null;

            let crop = this.crops.find(y => y.cropLocationId === this.cropLocationId);
            if(crop == null)
                return null;

            let notes = this.notes.filter(
                x => x.cropLocationId === this.cropLocationId
                && x.varietyId === variety.varietyId
            );

            let locationHasAlert = notes.some(
                y => y.alertLevel > 0 && !y.isClosed
            );

            let minAlertLevel = notes
                .filter(n=> !n.isClosed)
                .reduce((a, v) => {
                    if(v.alertLevel < a){
                        return v.alertLevel;
                    }

                    return a;
                }, 4);

            let locationAlertLevelColor = forecastingUtilService.getAlertLevelColor(minAlertLevel);

            return {
                locationName: crop.locationName,
                varietyName: variety.varietyName,
                locationHasAlert,
                locationAlertLevelColor
            };
        },
        title(){
            return `${this.item.locationName} - ${this.item.varietyName}`;
        },
        isDetailMode(){
            return this.mode === ForecastingItemModes.Detail;
        },
        isNotesMode(){
            return this.mode === ForecastingItemModes.Notes;
        },
        isProfileMode(){
            return this.mode === ForecastingItemModes.Profile;
        },
        cropForecasts() {
            return this.profileForecasts || this.forecasts.filter(
                x => x.cropLocationId == this.cropLocationId 
                && x.varietyId == this.varietyId
            );
        },
        cropNotes(){
            return this.notes.filter(
                x => x.cropLocationId == this.cropLocationId 
                && x.varietyId == this.varietyId
            );
        },
        isTabletMode(){
            return this.$vuetify.breakpoint.mdAndDown;
        }
    },
    methods: {
        async loadForecastsAndActualsForProfile() {
            if (this.varietyEdit.activeProfile != null && this.varietyEdit.activeProfile.CropLocationID) {                
                let startDate = forecastingUtilService.getFinancialStartDateFromProfile(this.varietyEdit.activeProfile, this.financialYearCalendarData);                
                let endDate = forecastingUtilService.getFinancialEndDateFromProfile(this.varietyEdit.activeProfile, this.financialYearCalendarData);
                try {
                    this.loading = true;
                    this.profileForecasts = await forecastingProfileApi.getCropForecasts(this.varietyEdit.activeProfile.CropLocationID, this.varietyEdit.activeProfile.VarietyID, startDate, endDate);
                } catch(err) {
                    console.log("Error", err);
                }

                this.loading = false;
            }
        }
    },
    created(){
        let variety = this.varieties.find(
            x => x.cropLocationId == this.cropLocationId 
            && x.varietyId == this.varietyId
        );
        this.varietyEdit = JSON.parse(JSON.stringify(variety));
        this.loadForecastsAndActualsForProfile(); 
        if(this.routeForecastMode) {
            this.mode = this.routeForecastMode;
        }
    }
}
</script>

<style scoped>
.flex-column {
    flex-direction: column!important;
}

.flex-row {
    flex-direction: row!important;
}

.d-flex {
    display: flex!important;
}

.flex-grow-1 {
    flex-grow: 1!important;
}

.flex-grow-0 {
    flex-grow: 0!important;
}
</style>